/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import { CURR_ENV } from '@/core/constants/appConfig/environment';
import { FALLBACK_STATIC_API_BASE_URL } from '@/core/constants/appConfig/environment';
import { EScriptEnv } from '@/core/constants/appConfig/types';
import { RecacheStorageHandler } from '@/core/data_handlers/storage_handler_cached';
import { Logger } from '@/shared/logger/logger';

// Threshold for time difference (in milliseconds)
const TIME_DIFF_THRESHOLD_MS = 250; // 250ms

type TimeData = {
  clientServerDiff: number;
  headers?: Record<string, string>;
  // When this preference was set
  localTimePreferenceSetAt?: number;
  timestamp: number;
  // Flag to indicate if local time should be used instead
  useLocalTime?: boolean;
};

// First declare fetchServerTime standalone before using it in TimeUtils
async function fetchServerTime(): Promise<TimeData> {
  // Skip API call and use local time when in local environment
  if (CURR_ENV === EScriptEnv.LOCAL) {
    Logger.debug('Using local time in local environment');
    return { clientServerDiff: 0, timestamp: Date.now() };
  }

  try {
    const clientTimeBefore = Date.now();
    const resp = await fetch(`${FALLBACK_STATIC_API_BASE_URL}/static2/time`, {
      cache: 'no-cache',
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
      method: 'HEAD',
    });
    const clientTimeAfter = Date.now();
    const serverTime = resp.headers.get('X-Server-Time');

    if (!serverTime) {
      Logger.debug('No server time header received');
      return { clientServerDiff: 0, timestamp: Date.now() };
    }

    // Extract IP-related headers
    const headers: Record<string, string> = {};
    [
      'X-Client-IP',
      'X-Client-IP-V4',
      'X-Client-IP-V6',
      'X-Forwarded-For',
      'X-Real-IP',
    ].forEach(header => {
      const value = resp.headers.get(header);
      if (value) {
        headers[header.toLowerCase()] = value;
      }
    });

    const serverTimestamp = Math.floor(Number.parseFloat(serverTime) * 1000);
    const clientTimeMid = Math.floor((clientTimeBefore + clientTimeAfter) / 2);
    const timeDiff = clientTimeMid - serverTimestamp;

    // Check if local time is close enough to server time
    const useLocalTime = Math.abs(timeDiff) <= TIME_DIFF_THRESHOLD_MS;

    if (useLocalTime) {
      Logger.debug('Server-client time difference is small, using local time', {
        threshold: TIME_DIFF_THRESHOLD_MS,
        timeDiff,
      });
    }

    return {
      clientServerDiff: timeDiff,
      headers,
      localTimePreferenceSetAt: Date.now(),
      timestamp: serverTimestamp,
      useLocalTime,
    };
  } catch (error) {
    Logger.debug('Error getting synced time:', error);
    return { clientServerDiff: 0, timestamp: Date.now() };
  }
}

export const TimeUtils = {
  _storageHandler: new RecacheStorageHandler<TimeData>(
    'timeData',
    {
      clientServerDiff: 0,
      headers: {},
      timestamp: 0,
      useLocalTime: false,
    },
    EBrowserStorageType.LOCALSTORAGE,
    5 * 60 * 1000, // 5 minutes cache
    fetchServerTime,
  ),

  clearTimeCache(): void {
    TimeUtils._storageHandler.set({
      headers: {},
      timestamp: 0,
      useLocalTime: false,
    });
  },

  disableCache: false,

  fetchServerTime, // Expose the standalone function

  async getCachedTime(): Promise<{
    headers?: Record<string, string>;
    time: number;
    useLocalTime?: boolean;
  }> {
    // Always use local time directly in local environment, bypass cache
    if (CURR_ENV === EScriptEnv.LOCAL) {
      Logger.debug('Using local time in local environment, bypassing cache');
      return { time: Date.now(), useLocalTime: true };
    }

    if (TimeUtils.disableCache) {
      const fallbackData = await fetchServerTime();
      return {
        headers: fallbackData.headers,
        time: fallbackData.timestamp,
        useLocalTime: fallbackData.useLocalTime,
      };
    }

    const clientTs = Date.now();

    try {
      const data = await TimeUtils._storageHandler.getAsync();

      // If we should use local time based on previous check, return current time
      if (data.useLocalTime) {
        Logger.debug(
          'Using local time based on previous server-client time comparison',
        );
        return {
          headers: data.headers,
          time: Date.now(),
          useLocalTime: true,
        };
      }

      const syncedTime = clientTs - data.clientServerDiff;

      return {
        headers: data.headers,
        time: syncedTime,
        useLocalTime: false,
      };
    } catch (error) {
      Logger.debug('Error fetching cached time', error);
      return { time: Date.now() };
    }
  },

  getCurrentEpochInMs(): number {
    return Date.now();
  },

  async getCurrentEpochInMsSynced(): Promise<number> {
    // In local environment, directly return current time without sync
    if (CURR_ENV === EScriptEnv.LOCAL) {
      return Date.now();
    }

    const result = await TimeUtils.getCachedTime();
    return result.time;
  },

  getCurrentEpochInSeconds(): number {
    return Math.floor(TimeUtils.getCurrentEpochInMs() / 1000);
  },

  async getCurrentEpochInSecondsSynced(): Promise<number> {
    // In local environment, directly return current time without sync
    if (CURR_ENV === EScriptEnv.LOCAL) {
      return Math.floor(Date.now() / 1000);
    }

    const result = await TimeUtils.getCurrentEpochInMsSynced();
    return Math.floor(result / 1000);
  },

  async getCurrentSyncedTime(): Promise<{
    clientServerDiff: number;
    clientTime: number;
    headers?: Record<string, string>;
    requestDuration: number;
    syncedTime: number;
    usingLocalTime?: boolean;
  } | null> {
    // In local environment, use local time with zero diff
    if (CURR_ENV === EScriptEnv.LOCAL) {
      const localTime = Date.now();
      Logger.debug('Using local time in local environment, no sync needed');
      return {
        clientServerDiff: 0,
        clientTime: localTime,
        requestDuration: 0,
        syncedTime: localTime,
        usingLocalTime: true,
      };
    }

    const startTime = performance.now();
    try {
      const serverTimeResult = await TimeUtils.getCachedTime();
      const requestDuration = performance.now() - startTime;
      const clientTime = Date.now();

      // If using local time, client and synced time will be the same
      const syncedTime = serverTimeResult.useLocalTime
        ? clientTime
        : serverTimeResult.time;
      const clientServerDiff = Math.abs(clientTime - syncedTime);

      Logger.debug('Time sync completed', {
        clientServerDiff,
        clientTime,
        headers: serverTimeResult.headers,
        requestDuration,
        serverTimeMs: syncedTime,
        usingLocalTime: serverTimeResult.useLocalTime,
      });

      return {
        clientServerDiff,
        clientTime,
        headers: serverTimeResult.headers,
        requestDuration,
        syncedTime,
        usingLocalTime: serverTimeResult.useLocalTime,
      };
    } catch (error) {
      Logger.debug('Error getting synced time', error);
      return null;
    }
  },
};
